// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import loadAccountInquiry from "@werner/App/Handlers/InvoiceHistory/loadAccountInquiry";
import { CustomAppState } from "@werner/App/Store/Reducers";
import { getStatementSummary } from "@werner/App/Store/Selectors/AccountInquirySelectors";
import { xIsAdministrator } from "@werner/App/Store/Selectors/GeneralSelectors";
import { EMPTY_VALUE } from "@werner/XDL/constants";
import { color, xScreen } from "@werner/XDL/Utilities";
import React, { FC, useEffect } from "react";
import { useDispatch } from "react-redux";
import { css } from "styled-components";

export interface InvoiceHistoryAccountInquiryStyles {
    container?: InjectableCss;
    loadingSpinner?: LoadingSpinnerProps;
    wrapper?: InjectableCss;
    heading?: TypographyPresentationProps;
    sectionStyles?: InjectableCss;
}

export const invoiceHistoryAccountInquiryStyles: InvoiceHistoryAccountInquiryStyles = {
    container: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: center;
        `,
    },
    wrapper: {
        css: css`
            border-top: 3px solid ${color("common.border")};
            padding-top: 12px;
            display: flex;
            align-items: flex-start;
            gap: 32px;
            margin-bottom: 32px;
            ${xScreen("lte", "sm")} {
                flex-direction: column;
                gap: 24px;
            }
        `,
    },
    heading: {
        variant: "h4",
        css: css`
            margin: 16px 0 8px 0;
        `,
    },
    sectionStyles: {
        css: css`
            display: flex;
            flex-direction: column;
            max-width: calc(100% / 3);
            flex: 1;

            ${xScreen("lte", "md")} {
                max-width: calc(50% - 24px);
                width: 100%;
                flex: 1;
            }
            ${xScreen("lte", "sm")} {
                max-width: 100%;
                width: 100%;
                flex: 1;
            }
        `,
    },
};

export interface TableRowProps {
    label: string;
    value?: string;
    boldText?: boolean;
}

export interface TableRowStyles {
    container?: InjectableCss;
    labelText?: TypographyPresentationProps;
    valueText?: TypographyPresentationProps;
}

const tableRowStyles: TableRowStyles = {
    container: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: space-between;
            min-height: 24px;
            margin-bottom: 4px;
        `,
    },
    labelText: {
        css: css`
            ${({ boldText }: any) => {
                if (boldText) {
                    return css`
                        font-weight: 500;
                    `;
                }
            }}
        `,
    },
    valueText: {
        css: css`
            ${({ boldText }: any) => {
                if (boldText) {
                    return css`
                        font-weight: 500;
                    `;
                }
            }}
        `,
    },
};

type Props = TableRowProps;
export const TableRow: FC<Props> = ({ label, value, boldText }) => {
    const styles = tableRowStyles;

    return (
        <StyledWrapper {...styles.container}>
            <Typography {...styles.labelText} {...{ boldText }}>
                {label}
            </Typography>
            <Typography {...styles.valueText} {...{ boldText }}>
                {value}
            </Typography>
        </StyledWrapper>
    );
};

const styles = invoiceHistoryAccountInquiryStyles;
const InvoiceHistoryAccountInquiry: FC<WidgetProps> = ({ id }) => {
    const dispatch = useDispatch();
    const { statementSummary, isLoading, currencySymbol, name, isAdministrator } = useAppSelector(state => {
        const session = getSession(state);
        const billTo = session.billToId ? state.data.billTos.byId[session.billToId] : null;

        return {
            statementSummary: getStatementSummary(state as CustomAppState).data,
            isLoading: getStatementSummary(state as CustomAppState).isLoading,
            currencySymbol: session.currency?.currencySymbol,
            language: state.context.session.language,
            name: billTo ? `${billTo.companyName} (#${billTo.customerNumber})` : "",
            isAdministrator: xIsAdministrator(state),
        };
    });

    // WES-1918: only users with "Administrator" role can view summary statments
    if (!isAdministrator) {
        return null;
    }

    useEffect(() => {
        dispatch(loadAccountInquiry({}));
    }, []);

    const getValueOrEmpty = (value?: string): string => {
        if (!value) {
            return EMPTY_VALUE;
        }
        return `${currencySymbol}${value}`;
    };

    if (!statementSummary && isLoading) {
        return (
            <StyledWrapper {...styles.container} data-test-selector="InvoiceHistory-Account-Inquiry-widget">
                <LoadingSpinner {...styles.loadingSpinner} />
            </StyledWrapper>
        );
    }

    if (!statementSummary && !isLoading) {
        return null;
    }

    return (
        <>
            <Typography {...styles.heading}>
                {translate("Statement Summary for")} {name}
            </Typography>
            <StyledWrapper {...styles.wrapper}>
                <StyledWrapper {...styles.sectionStyles}>
                    <TableRow label={translate("Terms")} value={statementSummary?.terms} />
                    <TableRow label={translate("Credit Limit")} value={statementSummary?.creditLimit} />
                    <TableRow label={translate("Available Credit")} value={statementSummary?.availableCredit} />
                    <TableRow label={translate("Last Purchase")} value={statementSummary?.lastPurchase} />
                    <TableRow label={translate("Last Payment")} value={statementSummary?.lastPayment} />
                    <TableRow label={translate("MTD Purchased")} value={statementSummary?.mtdPurchases} />
                    <TableRow label={translate("YTD Purchased")} value={statementSummary?.ytdPurchases} />
                    <TableRow label={translate("Payment Days")} value={statementSummary?.paymentDays} />
                </StyledWrapper>
                <StyledWrapper {...styles.sectionStyles}>
                    <TableRow label={translate("Future")} value={statementSummary?.future} />
                    <TableRow label={translate("Current")} value={statementSummary?.current} />
                    <TableRow label={translate("31-60")} value={statementSummary?.thirty} />
                    <TableRow label={translate("61-90")} value={statementSummary?.sixty} />
                    <TableRow label={translate("91-120")} value={statementSummary?.ninety} />
                    <TableRow label={translate("Over 120")} value={statementSummary?.oneTwenty} />
                    {/* To create an empty line */}
                    <TableRow label="" value="" />
                    <TableRow label="" value="" />
                    <TableRow label={translate("Total")} value={statementSummary?.arTotal} boldText={true} />
                </StyledWrapper>
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: InvoiceHistoryAccountInquiry,
    definition: {
        group: "Invoice History",
        displayName: "Invoice History Account Inquiry",
        allowedContexts: ["InvoiceHistoryPage"],
    },
};

export default widgetModule;
